import { useCallback } from 'react';

export const usePopoverHandlers = ({
  setIsOpen,
  setHoveredIndexes,
  timeoutRef,
  shouldCloseOnHover,
  popoverContext,
  onOptionClick,
  disableBlur,
  panelRef,
  updatePopoverPosition,
  submenuRefs
}) => {
  const handleMouseEnter = useCallback(
    ({ level, option, index }) => {
      if (!option.children?.length && option.disabled) return;

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      setHoveredIndexes((prev) => {
        if (prev[level] !== index) {
          // Close levels higher than current level when entering any level
          return {
            ...Object.fromEntries(Object.entries(prev).filter(([key]) => parseInt(key) <= level)),
            [level]: index
          };
        }
        return prev;
      });
    },
    [setHoveredIndexes, timeoutRef]
  );

  const handleMouseLeave = useCallback(
    ({ level, option, event }) => {
      if (!shouldCloseOnHover || (!option.children?.length && option.disabled)) return;

      // Check if mouse is still within any relevant panel
      const isMouseInMainPanel =
        event?.relatedTarget &&
        event.relatedTarget instanceof Node &&
        panelRef?.current?.contains(event.relatedTarget);

      // For submenus, check if mouse is still within any submenu using submenu refs
      let isMouseInSubmenu = false;
      if (level > 1 && submenuRefs?.current) {
        // Check all submenu refs to see if any contain the mouse target
        for (const submenuRef of Object.values(submenuRefs.current)) {
          if (submenuRef?.current?.contains(event.relatedTarget)) {
            isMouseInSubmenu = true;
            break;
          }
        }
      }

      const isMouseStillInPanel = isMouseInMainPanel || isMouseInSubmenu;

      if (isMouseStillInPanel) {
        // Mouse is still within a panel, don't close immediately
        return;
      } else {
        // Mouse left all panels, apply 500ms delay before closing
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        const timeout = (timeoutRef.current = setTimeout(() => {
          setHoveredIndexes((prev) => ({ ...prev, [level]: null }));
        }, 500));

        return () => clearTimeout(timeout);
      }
    },
    [shouldCloseOnHover, setHoveredIndexes, timeoutRef, panelRef, submenuRefs]
  );

  const handleOptionClick = useCallback(
    (option, handleClose, event) => {
      if (option.onClick) {
        event.stopPropagation();
        option.onClick(popoverContext?.context);
        handleClose();
        setIsOpen(false);
      }
      if (onOptionClick) {
        onOptionClick(option, handleClose);
      }
    },
    [popoverContext, onOptionClick, setIsOpen]
  );

  const onPopoverClose = useCallback(
    (event) => {
      if (event?.type === 'blur' && disableBlur) return;
      setIsOpen(false);
    },
    [disableBlur, setIsOpen, setHoveredIndexes]
  );

  return {
    handleMouseEnter,
    handleMouseLeave,
    handleOptionClick,
    onPopoverClose
  };
};
